/* eslint-disable eag/no-index */
import {z, ZodEffects, ZodNullable, ZodOptional, ZodString} from 'zod';

import {isNotNaN, isNumber} from 'ramda-adjunct';

import {accountingCustomerRequestBodySchema, InvoiceV4ResponseBodyRead} from '@dms/api/finance';
import {
  CustomFieldValueSchema,
  PriceResponseBody,
  SupplierResponseBodySchema,
  TaxDocumentCalculationResponseBody,
  TaxDocumentCalculationV2Response,
  VatTypeSchema,
  VatTypeSummaryResponseBody,
  VatTypeSummaryV2ResponseBody,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

export type CustomerType = 'operator' | 'recipient';

export type DocumentSummary =
  | InvoiceV4ResponseBodyRead['documentSummary']
  | TaxDocumentCalculationResponseBody
  | TaxDocumentCalculationV2Response;

export type ExchangeRateRatioDocumentSummary =
  | InvoiceV4ResponseBodyRead['exchangeRateRatioDocumentSummary']
  | TaxDocumentCalculationResponseBody
  | TaxDocumentCalculationV2Response;

export type VatTypeSummary = Record<
  string,
  | VatTypeSummaryV2ResponseBody
  | VatTypeSummaryResponseBody
  | {
      groupSummary: PriceResponseBody;
      additionalCalculation: PriceResponseBody | null;
      vatRate: string;
    }
>;

export const accountingPaymentInfo = z.object({
  paymentMethod: z.enum([
    'BANK_TRANSFER',
    'CARD',
    'CASH',
    'OFFSET',
    'FREE_OF_CHARGE',
    'CASH_ON_DELIVERY',
  ]),
  bankAccount: z.string().nullish(),
  bankName: z.string().nullish(),
  iban: z.string().nullish(),
  swift: z.string().nullish(),
  variableSymbol: z
    .string()
    .max(10)
    .refine((val) => !isNaN(Number(val)), {
      message: i18n.t('general.errors.number.integer'),
    })
    .refine((val) => Number(val) > 0, {
      message: i18n.t('general.errors.number.positive'),
    })
    .nullish(),
  constantSymbol: z
    .string()
    .min(4)
    .max(4)
    .refine((val) => !isNaN(Number(val)), {
      message: i18n.t('general.errors.number.integer'),
    })
    .refine((val) => Number(val) > 0, {
      message: i18n.t('general.errors.number.positive'),
    })
    .nullish(),
  specificSymbol: z
    .string()
    .max(10)
    .refine((val) => !isNaN(Number(val)), {
      message: i18n.t('general.errors.number.integer'),
    })
    .refine((val) => Number(val) > 0, {
      message: i18n.t('general.errors.number.positive'),
    })
    .nullish(),
});

const makeQuantitySchema = (predicate: (value: number) => boolean, message: string) =>
  z.preprocess(
    (val) => (isNumber(val) ? String(val) : (val as string)),
    z
      .string()
      .nonempty({message: i18n.t('errors.field_is_required')})
      .refine((val) => isNotNaN(Number(val)) && predicate(Number(val)), {message})
  ) as ZodEffects<ZodEffects<ZodString, string, string>>;

export const accountingInvoiceItemWithMandatoryFields = z.object({
  itemId: z.string(),
  description: z.string().nonempty({message: i18n.t('errors.field_is_required')}),
  unit: z.string(),
  isUnitPriceWithVat: z.boolean(),
  vatType: VatTypeSchema,
  priceWithVat: z.string().nullish(),
  priceWithoutVat: z.string().nullish(),
  type: z.enum(['standard', 'margin', 'discount']),
  relatedItemId: z.string().nullable(),
  name: z.string().nullable().optional(),
  quantity: makeQuantitySchema((val) => val > 0, i18n.t('general.errors.number.positive')),
  pricePerUnit: z.preprocess(
    (val): string | null | undefined => (isNumber(val) ? String(val) : (val as string)),
    z.string().nonempty({message: i18n.t('errors.field_is_required')})
  ) as ZodEffects<ZodString, string, string>,
});

export const accountingCorrectiveTaxDocumentInvoiceItem =
  accountingInvoiceItemWithMandatoryFields.extend({
    quantity: makeQuantitySchema((val) => val !== 0, i18n.t('general.errors.number.zero')),
  });

export const accountingInvoiceItem = accountingInvoiceItemWithMandatoryFields.extend({
  quantity: accountingInvoiceItemWithMandatoryFields.shape.quantity.nullable(),
  pricePerUnit: accountingInvoiceItemWithMandatoryFields.shape.pricePerUnit.nullable(),
});

export const accountingDocumentFormValuesSchema = z.object({
  submitType: z.enum(['CREATE_DRAFT', 'CREATE_NEW']).nullish(),
  documentSeriesId: z.string().nullish(),
  sendAfterCreation: z.boolean().nullish(),
  downloadAfterCreation: z.boolean().nullish(),
  issueDate: z.string().nullish(),
  dueDate: z.string().nullish(),
  dueSinceIssueDateInDays: z.number().min(0).nullish(),
  reason: z.string().nullish(),
  cashRegisterId: z.string().nullish(),
  dateOfReception: z.string().nullish(),
  dateOfTaxableSupply: z.string().nullish(),
  currency: z.string().nullish(),
  customer: accountingCustomerRequestBodySchema
    .extend({
      contractInformationId: z.string().optional(),
    })
    .nullish(),
  additionalCustomer: z
    .object({
      customer: accountingCustomerRequestBodySchema,
      contractInformationId: z.string(),
      type: z.enum(['operator', 'recipient']),
    })
    .nullish(),
  supplier: SupplierResponseBodySchema.nullish(),
  paymentInfo: accountingPaymentInfo,
  paymentType: z
    .enum([
      'income/current_payment',
      'income/deposit_payment',
      'income/invoice_payment',
      'income/cash_register_income',
      'income/cash_register_transfer',
      'expense/current_payment',
      'expense/deposit_payment_return',
      'expense/cash_register_expense',
      'expense/cash_register_transfer',
      'expense/invoice_payment',
    ])
    .nullish(),
  invoiceItems: z.array(accountingInvoiceItem),
  internalNote: z.string().nullish(),
  footerNote: z.string().nullish(),
  id: z.string().nullish(),
  description: z.string().nullish(),
  number: z.string().nullish(),
  isExchangeRateRatioEnabled: z.boolean(),
  exchangeRateRatio: z
    .object({
      amount: z
        .string()
        .refine((val) => ['1', '100', '1000'].includes(val || ''), {
          message: i18n.t('general.notifications.ratioAmountError'),
        })
        .nullish(),
      ratio: z.preprocess(
        (val): string | null | undefined => (isNumber(val) ? String(val) : (val as string)),
        z
          .string()
          .refine((val) => isNotNaN(Number(val)), {
            message: i18n.t('general.errors.number.integer'),
          })
          .refine((val) => Number(val) > 0, {
            message: i18n.t('general.errors.number.positive'),
          })
          .nullish()
      ) as ZodEffects<
        ZodOptional<ZodNullable<ZodEffects<ZodEffects<ZodString, string, string>, string, string>>>,
        string | null | undefined,
        string | null | undefined
      >,
      currency: z.string().nullish(),
      type: z.string().nullish(),
    })
    .nullish(),
  customFieldsPayload: z.array(CustomFieldValueSchema).optional(),
  isInternal: z.boolean().nullish(),
});

export type AccountingDocumentFormValues = z.infer<typeof accountingDocumentFormValuesSchema>;
export type InvoiceItem = z.infer<typeof accountingInvoiceItem>;
export type VatTypeSummaryType =
  | Record<string, VatTypeSummaryResponseBody | VatTypeSummaryV2ResponseBody>
  | Nullish;
